import  { useState, useEffect, useRef,useCallback } from "react";
import { format, parseISO, isAfter } from "date-fns";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  Paper,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
  Timeline as TimelineIcon,
  PriorityHigh as PriorityHighIcon,
} from "@mui/icons-material";
import { motion} from "framer-motion";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import { useSelector } from "react-redux";
import Layout from "../components/layout/Layout";
import CloseIcon from '@mui/icons-material/Close';
import TaskService from "../Services/TaskService";  


ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
);

const UserTasks = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  
  const token = useSelector((state) => state.auth.token);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all");
  const [selectedTask, setSelectedTask] = useState(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [dashboardTab, setDashboardTab] = useState(0);
  const [taskViewMode, setTaskViewMode] = useState(0); 


  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const statusConfig = {
    pending: {
      color: "warning",
      label: "Pending",
      icon: <HourglassEmptyIcon />,
    },
    "in-progress": { color: "info", label: "In Progress", icon: <WorkIcon /> },
    completed: {
      color: "success",
      label: "Completed",
      icon: <CheckCircleIcon />,
    },
    overdue: { color: "error", label: "Overdue", icon: <ErrorIcon /> },
  };

  const priorityConfig = {
    low: { color: "default", label: "Low" },
    medium: { color: "primary", label: "Medium" },
    high: { color: "warning", label: "High" },
    urgent: { color: "error", label: "Urgent" },
  };

const showSnackbar = useCallback((message, severity = "success") => {
  setSnackbar({
    open: true,
    message,
    severity,
  });
}, []);

  const fetchTasks = useCallback( async () => {
    try {
      setLoading(true);
      const tasksList = await TaskService.getTasks();
      
      const updatedTasks = tasksList.map((task) => {
        if (
          task.status !== "completed" &&
          isAfter(new Date(), parseISO(task.dueDate))
        ) {
          return { ...task, status: "overdue" };
        }
        return task;
      });
      setTasks(updatedTasks);
    } catch (error) {
      showSnackbar(`Failed to fetch tasks: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  },[showSnackbar]);
    useEffect(() => {
    if (token) {
      TaskService.setAuthToken(token);
      fetchTasks();
    }
  }, [token,fetchTasks]);

 


  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      const updatedTask = await TaskService.updateTaskStatus(taskId, newStatus);

      setTasks((prev) =>
        prev.map((task) => (task._id === taskId ? updatedTask : task))
      );

      if (selectedTask && selectedTask._id === taskId) {
        setSelectedTask(updatedTask);
      }

      showSnackbar("Task status updated successfully!");
    } catch (error) {
      console.error("Error updating task status:", error);
      showSnackbar("Failed to update task status", "error");
    }
  };

  const handleViewTask = async (task) => {
    try {
     
      const taskDetails = await TaskService.getTaskById(task._id);
      setSelectedTask(taskDetails);
      setOpenTaskDialog(true);
    } catch (error) {
      console.error("Error fetching task details:", error);
      showSnackbar("Failed to fetch task details", "error");
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !selectedTask) return;

    try {
      setIsSubmittingNote(true);
      const updatedTask = await TaskService.addTaskNote(
        selectedTask._id,
        noteContent
      );

      setTasks((prev) =>
        prev.map((task) => (task._id === updatedTask._id ? updatedTask : task))
      );

      setSelectedTask(updatedTask);
      setNoteContent("");
      showSnackbar("Note added successfully!");
    } catch (error) {
      console.error("Error adding note:", error);
      showSnackbar("Failed to add note", "error");
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedTask) return;

    try {
      setUploadingAttachment(true);
      const updatedTask = await TaskService.addTaskAttachment(
        selectedTask._id,
        file
      );

      setTasks((prev) =>
        prev.map((task) => (task._id === updatedTask._id ? updatedTask : task))
      );

      setSelectedTask(updatedTask);
      showSnackbar("Attachment uploaded successfully!");
    } catch (error) {
      console.error("Error uploading attachment:", error);
      showSnackbar("Failed to upload attachment", "error");
    } finally {
      setUploadingAttachment(false);
     
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getAssignerName = (task) => {
    if (typeof task.assignedBy === "object" && task.assignedBy !== null) {
      return (
        task.assignedBy.name ||
        task.assignedBy.username ||
        task.assignedBy.email ||
        "Professional"
      );
    }
    return "Professional";
  };

  
  const getStatusChartData = () => {
    const statusCounts = {
      pending: 0,
      "in-progress": 0,
      completed: 0,
      overdue: 0,
    };

    tasks.forEach((task) => {
      statusCounts[task.status]++;
    });

    return {
      labels: Object.keys(statusCounts).map(
        (status) => statusConfig[status].label
      ),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: [
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.error.main,
          ],
          borderColor: [
            theme.palette.warning.dark,
            theme.palette.info.dark,
            theme.palette.success.dark,
            theme.palette.error.dark,
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const getPriorityChartData = () => {
    const priorityCounts = {
      low: 0,
      medium: 0,
      high: 0,
      urgent: 0,
    };

    tasks.forEach((task) => {
      priorityCounts[task.priority]++;
    });

    return {
      labels: ["Low", "Medium", "High", "Urgent"],
      datasets: [
        {
          label: "Tasks by Priority",
          data: Object.values(priorityCounts),
          backgroundColor: [
            alpha(theme.palette.grey[500], 0.8),
            alpha(theme.palette.primary.main, 0.8),
            alpha(theme.palette.warning.main, 0.8),
            alpha(theme.palette.error.main, 0.8),
          ],
          borderColor: theme.palette.background.paper,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Tasks by Priority",
      },
    },
  };

  
  const taskContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const taskItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  
  const renderDashboard = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={dashboardTab}
            onChange={(e, newValue) => setDashboardTab(newValue)}
          >
            <Tab icon={<DashboardIcon />} label="OVERVIEW" />
            <Tab icon={<TimelineIcon />} label="PROGRESS" />
          </Tabs>
        </Box>

        {dashboardTab === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Task Status Distribution
              </Typography>
              <Box sx={{ height: 280 }}>
                <Doughnut data={getStatusChartData()} options={chartOptions} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Task Priorities
              </Typography>
              <Box sx={{ height: 280 }}>
                <Bar data={getPriorityChartData()} options={barChartOptions} />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Task Progress
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(statusConfig).map(([status, config]) => (
                  <Grid item xs={6} sm={3} key={status}>
                    <Paper
                      sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        bgcolor: alpha(theme.palette[config.color].main, 0.1),
                        borderRadius: 2,
                      }}
                      elevation={1}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette[config.color].main,
                          mb: 1,
                        }}
                      >
                        {config.icon}
                      </Avatar>
                      <Typography
                        variant="h4"
                        color={`${config.color}.main`}
                        sx={{ fontWeight: "bold" }}
                      >
                        {tasks.filter((task) => task.status === status).length}
                      </Typography>
                      <Typography variant="subtitle2" color="textSecondary">
                        {config.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Recent Activity
                </Typography>
                <List>
                  {tasks
                    .sort(
                      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
                    )
                    .slice(0, 5)
                    .map((task) => (
                      <ListItem
                        key={task._id}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: alpha(
                            theme.palette[statusConfig[task.status].color].main,
                            0.05
                          ),
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor:
                                theme.palette[statusConfig[task.status].color]
                                  .main,
                            }}
                          >
                            {statusConfig[task.status].icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={task.title}
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="textPrimary"
                              >
                                {statusConfig[task.status].label}
                              </Typography>
                              {` — Updated ${format(
                                new Date(task.updatedAt),
                                "MMM dd, yyyy"
                              )}`}
                            </>
                          }
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewTask(task)}
                        >
                          View
                        </Button>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </motion.div>
  );

  return (
    <Layout>
      <Box
        sx={{
          container: true,
          maxWidth: "lg",
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 8 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              My Tasks
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                startIcon={<DashboardIcon />}
                variant={taskViewMode === 0 ? "contained" : "outlined"}
                onClick={() => setTaskViewMode(0)}
              >
                Dashboard
              </Button>
              <Button
                startIcon={<AssignmentIcon />}
                variant={taskViewMode === 1 ? "contained" : "outlined"}
                onClick={() => setTaskViewMode(1)}
              >
                Task List
              </Button>
            </Box>
          </Box>
        </motion.div>

        {taskViewMode === 0 ? (
          renderDashboard()
        ) : (
          <Grid container spacing={4}>
           
            <Grid item xs={12} lg={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    Task Information
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tasks assigned to you
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "medium", color: "primary.main" }}
                      >
                        {tasks.length}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Pending tasks
                      </Typography>
                      <Typography variant="h5" sx={{ color: "warning.main" }}>
                        {
                          tasks.filter((task) => task.status === "pending")
                            .length
                        }
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        In progress
                      </Typography>
                      <Typography variant="h5" sx={{ color: "info.main" }}>
                        {
                          tasks.filter((task) => task.status === "in-progress")
                            .length
                        }
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Completed tasks
                      </Typography>
                      <Typography variant="h5" sx={{ color: "success.main" }}>
                        {
                          tasks.filter((task) => task.status === "completed")
                            .length
                        }
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Overdue tasks
                      </Typography>
                      <Typography variant="h5" sx={{ color: "error.main" }}>
                        {
                          tasks.filter((task) => task.status === "overdue")
                            .length
                        }
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 4,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Button
                      variant={viewMode === "all" ? "contained" : "outlined"}
                      color="primary"
                      fullWidth
                      onClick={() => handleViewModeChange("all")}
                      startIcon={<FilterListIcon />}
                    >
                      All Tasks
                    </Button>
                    <Button
                      variant={
                        viewMode === "pending" ? "contained" : "outlined"
                      }
                      color="warning"
                      fullWidth
                      onClick={() => handleViewModeChange("pending")}
                      startIcon={<HourglassEmptyIcon />}
                    >
                      Pending Tasks
                    </Button>
                    <Button
                      variant={
                        viewMode === "in-progress" ? "contained" : "outlined"
                      }
                      color="info"
                      fullWidth
                      onClick={() => handleViewModeChange("in-progress")}
                      startIcon={<WorkIcon />}
                    >
                      In Progress
                    </Button>
                    <Button
                      variant={
                        viewMode === "completed" ? "contained" : "outlined"
                      }
                      color="success"
                      fullWidth
                      onClick={() => handleViewModeChange("completed")}
                      startIcon={<CheckCircleIcon />}
                    >
                      Completed
                    </Button>
                    <Button
                      variant={
                        viewMode === "overdue" ? "contained" : "outlined"
                      }
                      color="error"
                      fullWidth
                      onClick={() => handleViewModeChange("overdue")}
                      startIcon={<ErrorIcon />}
                    >
                      Overdue
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column: Task List */}
            <Grid item xs={12} lg={8}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                      pb: 2,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6">
                      {viewMode === "all"
                        ? "All Tasks"
                        : viewMode === "pending"
                        ? "Pending Tasks"
                        : viewMode === "in-progress"
                        ? "In Progress Tasks"
                        : viewMode === "completed"
                        ? "Completed Tasks"
                        : "Overdue Tasks"}
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : tasks.filter(
                      (task) => viewMode === "all" || task.status === viewMode
                    ).length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 6,
                      }}
                    >
                      <Typography variant="h6" color="textSecondary">
                        No {viewMode === "all" ? "" : viewMode} tasks found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {viewMode === "all"
                          ? "You don't have any tasks assigned to you yet"
                          : `You don't have any ${viewMode} tasks at the moment`}
                      </Typography>
                    </Box>
                  ) : (
                    <motion.div
                      variants={taskContainerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {tasks
                          .filter(
                            (task) =>
                              viewMode === "all" || task.status === viewMode
                          )
                          .map((task) => (
                            <motion.div
                              key={task._id}
                              variants={taskItemVariants}
                              whileHover={{ scale: 1.01 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <Card
                                variant="outlined"
                                sx={{
                                  borderLeft: 5,
                                  borderColor: `${
                                    statusConfig[task.status].color
                                  }.main`,
                                  "&:hover": {
                                    boxShadow: 3,
                                  },
                                }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography variant="h6">
                                      {task.title}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Chip
                                        label={
                                          priorityConfig[task.priority]
                                            ?.label || task.priority
                                        }
                                        color={
                                          priorityConfig[task.priority]
                                            ?.color || "default"
                                        }
                                        size="small"
                                        icon={<PriorityHighIcon />}
                                      />
                                      <Chip
                                        label={
                                          statusConfig[task.status]?.label ||
                                          task.status
                                        }
                                        color={
                                          statusConfig[task.status]?.color ||
                                          "default"
                                        }
                                        size="small"
                                        icon={statusConfig[task.status]?.icon}
                                      />
                                    </Box>
                                  </Box>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2 }}
                                  >
                                    {task.description.length > 150
                                      ? `${task.description.substring(
                                          0,
                                          150
                                        )}...`
                                      : task.description}
                                  </Typography>

                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2">
                                        <strong>Due:</strong>{" "}
                                        <Box
                                          component="span"
                                          sx={{
                                            color:
                                              new Date(task.dueDate) <
                                                new Date() &&
                                              task.status !== "completed"
                                                ? "error.main"
                                                : "inherit",
                                          }}
                                        >
                                          {format(
                                            new Date(task.dueDate),
                                            "MMM dd, yyyy"
                                          )}
                                        </Box>
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2">
                                        <strong>Assigned by:</strong>{" "}
                                        {getAssignerName(task)}
                                      </Typography>
                                    </Grid>
                                  </Grid>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mt: 2,
                                    }}
                                  >
                                    <FormControl sx={{ minWidth: 120 }}>
                                      <InputLabel>Status</InputLabel>
                                      <Select
                                        value={task.status}
                                        label="Status"
                                        onChange={(e) =>
                                          handleStatusUpdate(
                                            task._id,
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="pending">
                                          Pending
                                        </MenuItem>
                                        <MenuItem value="in-progress">
                                          In Progress
                                        </MenuItem>
                                        <MenuItem value="completed">
                                          Completed
                                        </MenuItem>
                                      </Select>
                                    </FormControl>

                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Tooltip title="View Details">
                                        <IconButton
                                          onClick={() => handleViewTask(task)}
                                          color="primary"
                                          size="small"
                                        >
                                          <VisibilityIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip
                                        title={`${
                                          task.notes?.length || 0
                                        } comments`}
                                      >
                                        <IconButton size="small">
                                          <CommentIcon />
                                          <Typography
                                            variant="caption"
                                            sx={{ ml: 0.5 }}
                                          >
                                            {task.notes?.length || 0}
                                          </Typography>
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip
                                        title={`${
                                          task.attachments?.length || 0
                                        } attachments`}
                                      >
                                        <IconButton size="small">
                                          <AttachFileIcon />
                                          <Typography
                                            variant="caption"
                                            sx={{ ml: 0.5 }}
                                          >
                                            {task.attachments?.length || 0}
                                          </Typography>
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                      </Box>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Task Detail Dialog */}
        <Dialog
          open={openTaskDialog}
          onClose={() => setOpenTaskDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedTask?.title}
            <IconButton
              aria-label="close"
              onClick={() => setOpenTaskDialog(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTask && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      fontWeight="medium"
                    >
                      Description
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {selectedTask.description}
                    </Typography>

                    <Box sx={{ mt: 4 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        fontWeight="medium"
                      >
                        Task Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={
                              statusConfig[selectedTask.status]?.label ||
                              selectedTask.status
                            }
                            color={
                              statusConfig[selectedTask.status]?.color ||
                              "default"
                            }
                            size="small"
                            icon={statusConfig[selectedTask.status]?.icon}
                            sx={{ mt: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Priority
                          </Typography>
                          <Chip
                            label={
                              priorityConfig[selectedTask.priority]?.label ||
                              selectedTask.priority
                            }
                            color={
                              priorityConfig[selectedTask.priority]?.color ||
                              "default"
                            }
                            size="small"
                            icon={<PriorityHighIcon />}
                            sx={{ mt: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            <ScheduleIcon
                              fontSize="small"
                              sx={{ verticalAlign: "middle", mr: 1 }}
                            />
                            {format(
                              new Date(selectedTask.dueDate),
                              "MMM dd, yyyy"
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Assigned By
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {getAssignerName(selectedTask)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ mt: 4 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        fontWeight="medium"
                      >
                        Attachments ({selectedTask.attachments?.length || 0})
                      </Typography>

                      {selectedTask.attachments?.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {selectedTask.attachments.map((attachment, index) => (
                            <Chip
                              key={index}
                              label={attachment.filename}
                              icon={<AttachFileIcon />}
                              onClick={() =>
                                window.open(attachment.url, "_blank")
                              }
                              sx={{ mb: 1 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No attachments
                        </Typography>
                      )}

                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={
                            uploadingAttachment ? (
                              <CircularProgress size={16} />
                            ) : (
                              <AttachFileIcon />
                            )
                          }
                          onClick={handleFileSelect}
                          disabled={uploadingAttachment}
                        >
                          {uploadingAttachment
                            ? "Uploading..."
                            : "Upload Attachment"}
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          style={{ display: "none" }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        fontWeight="medium"
                      >
                        Task Notes ({selectedTask.notes?.length || 0})
                      </Typography>

                      <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 3 }}>
                        {selectedTask.notes && selectedTask.notes.length > 0 ? (
                          selectedTask.notes.map((note, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 2,
                                pb: 2,
                                borderBottom:
                                  index !== selectedTask.notes.length - 1
                                    ? 1
                                    : 0,
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "medium" }}
                              >
                                {note.addedBy?.name ||
                                  note.addedBy?.username ||
                                  "User"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {format(
                                  new Date(note.createdAt),
                                  "MMM dd, yyyy 'at' h:mm a"
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {note.content}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No notes for this task
                          </Typography>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" gutterBottom>
                        Add Note
                      </Typography>
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Type your note here..."
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={
                          isSubmittingNote ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SendIcon />
                          )
                        }
                        onClick={handleAddNote}
                        disabled={!noteContent.trim() || isSubmittingNote}
                      >
                        {isSubmittingNote ? "Sending..." : "Add Note"}
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTaskDialog(false)}>Close</Button>
            {selectedTask && selectedTask.status !== "completed" && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleStatusUpdate(selectedTask._id, "completed");
                  setOpenTaskDialog(false);
                }}
              >
                Mark as Completed
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default UserTasks;
